import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { fmtMoney, fmtDate, eventLocalTime, eventLocalDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { KPI_FIELDS } from "@/pages/Kpis";
import { FilePlus2, Sparkles, Trophy, Target, Flame, Phone, MessageSquare, ListChecks, CalendarDays, Clock, MapPin } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { LEAD_SOURCES } from "@/pages/Leads";
import { OnboardingChecklist, WelcomeModal } from "@/components/Onboarding";
import PullToRefresh from "@/components/PullToRefresh";
import PushPrompt from "@/components/PushPrompt";
import AnnouncementBanner from "@/components/AnnouncementBanner";

const PROMPTS = [
  "What are we working on today?",
  "What's on the agenda today?",
  "What's the plan for today?",
  "What are we tackling today?",
  "What's the focus today?",
  "What are we getting after today?",
  "What's on deck today?",
  "What are we building today?",
  "What's the move today?",
  "What are we chasing today?",
  "Where are we putting our energy today?",
  "What's the priority today?",
  "What are we knocking out today?",
  "What's the mission today?",
  "What's the win for today?",
];

function pickPrompt() {
  // Stable for the session, fresh per sign-in
  const cached = sessionStorage.getItem("wu_dash_prompt");
  if (cached) return cached;
  const choice = PROMPTS[Math.floor(Math.random() * PROMPTS.length)];
  sessionStorage.setItem("wu_dash_prompt", choice);
  return choice;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [recap, setRecap] = useState(null);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadStreak, setLeadStreak] = useState(null);
  const [kpiStreaks, setKpiStreaks] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    const today = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 60);
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    await Promise.allSettled([
      api.get("/deals").then((r) => setDeals(r.data)),
      api.get("/kpis/weekly-recap").then((r) => setRecap(r.data)),
      api.get("/kpis/summary").then((r) => setKpiSummary(r.data)),
      api.get("/leads").then((r) => setLeads(r.data)),
      api.get("/leads/streak").then((r) => setLeadStreak(r.data)),
      api.get("/kpis/streaks").then((r) => setKpiStreaks(r.data)),
      api.get("/events", { params: { start: ymd(today), end: ymd(end) } }).then((r) => setUpcomingEvents(r.data || [])),
    ]);
  }, []);

  useEffect(() => {
    loadAll().finally(() => setLoading(false));
  }, [loadAll]);

  const onRefresh = async () => {
    await loadAll();
  };

  const recent = deals.slice(0, 5);
  const prompt = pickPrompt();

  return (
    <PullToRefresh onRefresh={onRefresh}>
    <div data-testid="dashboard-page">
      <WelcomeModal />
      <div className="mb-10 sm:mb-12">
        <div className="section-tag mb-4"><span className="dot" /> Welcome back</div>
        <h1 className="h-display text-3xl sm:text-5xl">
          Hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.<br />
          <span style={{ color: "var(--text-muted)" }} data-testid="dashboard-prompt">{prompt}</span>
        </h1>
      </div>

      <AnnouncementBanner />

      <PushPrompt />

      <div className="grid md:grid-cols-2 gap-6 mb-10" data-testid="dashboard-plan-events">
        <DailyActionPlan leads={leads} deals={deals} kpiStreaks={kpiStreaks} kpiSummary={kpiSummary} />
        <UpcomingEvents events={upcomingEvents} />
      </div>

      <OnboardingChecklist />

      <KpiStrip summary={kpiSummary} />

      <WeeklyWins recap={recap} />

      <StreakStrip leadStreak={leadStreak} kpiStreaks={kpiStreaks} />

      <SourcePerformance leads={leads} />

      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Activity</div>
          <h2 className="h-display text-2xl">Recent submissions</h2>
        </div>
      </div>

      {loading ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading...</div>
      ) : recent.length === 0 ? (
        <div className="surface-card p-10 text-center">
          <p className="text-lg mb-2">No deals yet.</p>
          <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
            Submit your first deal. Your coach is watching.
          </p>
          <Link to="/submit" className="btn-primary inline-flex items-center gap-2" data-testid="empty-submit-button">
            <FilePlus2 size={16} /> Submit a deal
          </Link>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <div className="cards-on-phone divide-y" style={{ borderColor: "var(--border)" }}>
            {recent.map((d) => (
              <Link
                key={d.id}
                to={`/deals/${d.id}`}
                className="block p-4"
                data-testid={`deal-card-${d.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold text-sm leading-snug">{d.property_address}</div>
                  <StatusBadge status={d.status} />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                  <span>{d.submission_type === "jv" ? "JV" : "ANALYZE"}</span>
                  <span>ARV {fmtMoney(d.arv)}</span>
                  <span>Ask {fmtMoney(d.asking_price)}</span>
                  <span>{fmtDate(d.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="overflow-x-auto table-on-desktop">
            <table className="wu-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Type</th>
                  <th>ARV</th>
                  <th>Asking</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => (window.location.href = `/deals/${d.id}`)}
                    data-testid={`deal-row-${d.id}`}
                  >
                    <td>{d.property_address}</td>
                    <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                      {d.submission_type === "jv" ? "JV" : "ANALYZE"}
                    </td>
                    <td className="font-mono">{fmtMoney(d.arv)}</td>
                    <td className="font-mono">{fmtMoney(d.asking_price)}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td className="text-sm" style={{ color: "var(--text-muted)" }}>{fmtDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}

const KPI_LABELS = Object.fromEntries(KPI_FIELDS.map((f) => [f.key, f.label]));

function DailyActionPlan({ leads, deals, kpiStreaks, kpiSummary }) {
  const today = new Date().toISOString().split("T")[0];
  const items = [];

  // 1. Follow-ups due today
  const followups = (leads || []).filter(
    (l) => l.next_follow_up && l.next_follow_up <= today && !["under_contract", "dead"].includes(l.status)
  );
  if (followups.length > 0) {
    items.push({
      icon: Phone,
      title: `${followups.length} follow-up${followups.length === 1 ? "" : "s"} to make`,
      detail: followups.slice(0, 2).map((l) => l.seller_name).join(", ") + (followups.length > 2 ? `, +${followups.length - 2} more` : ""),
      href: "/leads",
      testid: "action-followups",
    });
  }

  // 2. Coach replies waiting (unread)
  const unreadDeals = (deals || []).filter((d) => d.is_unread);
  if (unreadDeals.length > 0) {
    items.push({
      icon: MessageSquare,
      title: `${unreadDeals.length} coach repl${unreadDeals.length === 1 ? "y" : "ies"} to read`,
      detail: unreadDeals.slice(0, 2).map((d) => d.property_address).join(" · "),
      href: "/deals",
      testid: "action-coach-replies",
    });
  }

  // 3. KPI streak in jeopardy (didn't log today AND has streak)
  const streakDays = kpiStreaks?.daily_streak || 0;
  const today_progress = kpiSummary?.today?.activity_score || 0;
  if (streakDays >= 3 && today_progress === 0) {
    items.push({
      icon: Flame,
      title: `Your ${streakDays}-day KPI streak ends at midnight`,
      detail: "Log today's activity to keep it alive",
      href: "/kpis",
      testid: "action-streak-warning",
      urgent: true,
    });
  }

  // 4. Weekly pace check
  const weekProgress = kpiStreaks?.week_progress || {};
  const goals = kpiStreaks?.goals || {};
  const callsBehind = (goals.calls_made || 0) - (weekProgress.calls_made || 0);
  const dayOfWeek = new Date().getDay() || 7; // Mon=1..Sun=7
  const expectedPace = ((goals.calls_made || 0) * dayOfWeek) / 7;
  if (goals.calls_made && weekProgress.calls_made < expectedPace * 0.7) {
    items.push({
      icon: Target,
      title: `Behind pace on calls. ${callsBehind} to hit weekly goal`,
      detail: `You're at ${weekProgress.calls_made || 0} / ${goals.calls_made}. Pick up the phone.`,
      href: "/kpis",
      testid: "action-calls-behind",
    });
  }

  // 5. Empty-state fallback
  if (items.length === 0) {
    items.push({
      icon: Sparkles,
      title: "You're clear for today",
      detail: "No urgent follow-ups, no unread coach notes. Make outbound calls or hunt new leads.",
      href: "/leads",
      testid: "action-clear",
    });
  }

  return (
    <div className="surface-card p-4 sm:p-6 h-full" style={{ borderColor: "var(--border-lime)" }} data-testid="daily-action-plan">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks size={16} style={{ color: "var(--lime)" }} />
        <h3 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
          Today&apos;s action plan
        </h3>
      </div>
      <div className="space-y-2">
        {items.slice(0, 3).map((it, i) => (
          <Link
            key={it.testid}
            to={it.href}
            className="flex items-start gap-3 p-3 rounded-2xl transition hover:opacity-90"
            style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
            data-testid={it.testid}
          >
            <div className="flex-shrink-0 mt-0.5">
              <it.icon size={18} style={{ color: it.urgent ? "#f97316" : "var(--lime)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                #{i + 1}
              </div>
              <div className="font-medium text-sm leading-snug mb-0.5">{it.title}</div>
              <div className="text-xs leading-snug" style={{ color: "var(--text-muted)" }}>{it.detail}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StreakStrip({ leadStreak, kpiStreaks }) {
  const lead = leadStreak?.streak || 0;
  const kpi = kpiStreaks?.daily_streak || 0;
  if (lead === 0 && kpi === 0) return null;

  const items = [];
  if (kpi > 0) items.push({
    n: kpi,
    label: "KPI heat streak",
    sub: kpi >= 30 ? "🔥 You're unstoppable." : kpi >= 14 ? "Two weeks deep." : kpi >= 7 ? "Solid week." : "Keep it going.",
    href: "/kpis",
  });
  if (lead > 0) items.push({
    n: lead,
    label: "Lead-add streak",
    sub: lead >= 30 ? "🔥 You're unstoppable." : lead >= 14 ? "Momentum is real." : lead >= 7 ? "Don't break it." : "Keep it going.",
    href: "/leads",
  });

  return (
    <div className={`grid gap-4 mb-8 ${items.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"}`} data-testid="streak-strip">
      {items.map((it) => (
        <Link
          key={it.label}
          to={it.href}
          className="surface-card p-5 flex items-center gap-4 transition hover:opacity-90"
          data-testid={`streak-${it.label.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <Flame size={28} className="flex-shrink-0" style={{ color: "#f97316" }} />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs uppercase tracking-wider mb-0.5" style={{ color: "var(--lime)" }}>
              {it.label}
            </div>
            <div className="text-lg truncate">
              <span className="font-mono font-semibold" style={{ color: "var(--lime)" }}>{it.n}-day streak</span>
              <span className="text-sm ml-2" style={{ color: "var(--text-muted)" }}>· {it.sub}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function SourcePerformance({ leads }) {  if (!leads || leads.length === 0) return null;

  // Aggregate per source: count, under_contract count, dead count
  const data = LEAD_SOURCES.map((s) => {
    const sourceLeads = leads.filter((l) => l.source === s.value);
    const total = sourceLeads.length;
    const won = sourceLeads.filter((l) => l.status === "under_contract").length;
    const dead = sourceLeads.filter((l) => l.status === "dead").length;
    const conv = total > 0 ? Math.round((won / total) * 100) : 0;
    return { source: s.label, total, won, dead, conv };
  }).filter((d) => d.total > 0);

  if (data.length === 0) return null;

  return (
    <Link to="/leads" className="block mb-14" data-testid="source-performance">
      <div className="flex items-center mb-4">
        <div className="flex items-center gap-2">
          <Target size={14} style={{ color: "var(--lime)" }} />
          <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
            Lead source performance
          </h2>
        </div>
      </div>
      <div className="surface-card p-6">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {data.map((d) => (
            <div key={d.source} className="p-3 rounded-2xl" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }} data-testid={`source-stat-${d.source}`}>
              <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{d.source}</div>
              <div className="font-mono text-xl">{d.total}</div>
              <div className="font-mono text-xs mt-1" style={{ color: d.conv >= 10 ? "var(--lime)" : "var(--text-muted)" }}>
                {d.conv}% under contract
              </div>
            </div>
          ))}
        </div>
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <XAxis dataKey="source" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                contentStyle={{ background: "#141414", border: "1px solid #2a2a2a", borderRadius: 8, fontSize: 12, color: "#f5f5f5" }}
                labelStyle={{ color: "#C5E26A" }}
                itemStyle={{ color: "#f5f5f5" }}
                formatter={(value, name) => [value, name === "total" ? "Total leads" : name === "won" ? "Under contract" : "Dead"]}
              />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill="#3a3a3a" />)}
              </Bar>
              <Bar dataKey="won" radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill="#C5E26A" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Link>
  );
}

function KpiStrip({ summary }) {
  if (!summary) return null;
  const t = summary.totals || {};
  return (
    <Link to="/kpis" className="block mb-14" data-testid="dashboard-kpi-strip">
      <div className="flex items-center mb-4">
        <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
          Your KPIs, all time
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-8 gap-2 sm:gap-3">
        {KPI_FIELDS.map((f) => {
          const Icon = f.icon;
          const isProfit = f.key === "profit_earned";
          const raw = t[f.key] || 0;
          const key4 = ["calls_made", "leads_generated", "contracts_signed", "profit_earned"].includes(f.key);
          return (
            <div
              key={f.key}
              className={`surface-card p-3 sm:p-4 ${key4 ? "" : "hidden md:block"}`}
              data-testid={`dashboard-kpi-${f.key}`}
            >
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                <Icon size={12} style={{ color: "var(--lime)" }} />
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-wider leading-tight" style={{ color: "var(--text-muted)" }}>{f.label}</span>
              </div>
              <div className="stat-number text-xl sm:text-xl" style={{ color: raw ? "var(--text)" : "var(--text-dim)" }}>
                {raw === 0 ? "—" : isProfit ? fmtMoney(raw) : raw.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
      <div className="md:hidden mt-3 text-sm" style={{ color: "var(--lime)" }} data-testid="dashboard-kpi-see-all">
        See all KPIs →
      </div>
    </Link>
  );
}

function WeeklyWins({ recap }) {
  if (!recap || (!recap.has_activity && recap.goal_items.length === 0)) return null;
  const items = recap.goal_items;
  const allHit = items.length > 0 && recap.goals_hit === items.length;
  const fmtVal = (m, v) => (m === "profit_earned" ? fmtMoney(v) : Number(v).toLocaleString());
  const activeTotals = KPI_FIELDS.filter((f) => (recap.totals[f.key] || 0) > 0);

  return (
    <Link to="/kpis" className="surface-card block p-6 mb-14 transition hover:opacity-95" style={allHit ? { borderColor: "var(--border-lime)" } : undefined} data-testid="weekly-wins-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Trophy size={14} style={{ color: "var(--lime)" }} />
          <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>Last week&apos;s wins</h2>
          <span className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>
            · {fmtDate(recap.week_start)} – {fmtDate(recap.week_end)}
          </span>
        </div>
      </div>

      {items.length > 0 ? (
        <>
          <p className="text-sm mb-4" style={{ color: "var(--text)" }} data-testid="weekly-wins-headline">
            {allHit
              ? "You hit every goal last week. Keep the streak alive."
              : `You hit ${recap.goals_hit} of ${items.length} goals last week.`}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {items.map((i) => {
              const hit = i.total >= i.target;
              return (
                <div
                  key={i.metric}
                  className="px-4 py-3 rounded-2xl"
                  style={{ background: "var(--surface2)", border: `1px solid ${hit ? "var(--border-lime)" : "var(--border)"}` }}
                  data-testid={`weekly-wins-goal-${i.metric}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                    {KPI_LABELS[i.metric] || i.metric}
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-lg" style={{ color: hit ? "var(--lime)" : "var(--text)" }}>
                      {fmtVal(i.metric, i.total)}
                      <span className="text-xs" style={{ color: "var(--text-dim)" }}> / {fmtVal(i.metric, i.target)}</span>
                    </span>
                    <span className="font-mono text-xs" style={{ color: hit ? "var(--success)" : "var(--orange)" }}>
                      {i.pct >= 100 ? `+${i.pct - 100}% over` : `${i.pct}% of goal`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="weekly-wins-totals">
          {activeTotals.map((f) => (
            <span
              key={f.key}
              className="px-3 py-1.5 rounded-full font-mono text-xs"
              style={{ background: "var(--surface2)", border: "1px solid var(--border)", color: "var(--text)" }}
            >
              {fmtVal(f.key, recap.totals[f.key])} {f.label.toLowerCase()}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

const EVENT_TYPE_COLOR = {
  appointment: "#60a5fa",
  follow_up: "#f5a623",
  call: "#C5E26A",
  other: "#888888",
};
const EVENT_TYPE_LABEL = {
  appointment: "Appointment",
  follow_up: "Follow-up",
  call: "Call",
  other: "Event",
};

function formatEventDate(ymd) {
  // ymd is YYYY-MM-DD; render relative: Today / Tomorrow / weekday + short date
  const [y, m, d] = ymd.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff >= 2 && diff <= 6) return target.toLocaleDateString(undefined, { weekday: "long" });
  return target.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function UpcomingEvents({ events }) {
  const upcoming = (events || []).slice(0, 3);
  return (
    <div className="surface-card p-4 sm:p-6 h-full" data-testid="dashboard-upcoming-events">
      <div className="flex items-center mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} style={{ color: "var(--lime)" }} />
          <h3 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
            Upcoming
          </h3>
        </div>
      </div>

      {upcoming.length === 0 ? (
        <div className="py-4 text-center">
          <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
            Nothing scheduled. Block out time for your next call or follow-up.
          </p>
          <Link to="/calendar" className="btn-ghost text-xs inline-flex items-center gap-2">
            <CalendarDays size={12} /> Add an event
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {upcoming.map((ev) => (
            <Link
              key={ev.id}
              to={ev.source === "lead" && ev.source_ref ? `/leads` : "/calendar"}
              className="flex items-center gap-3 p-3 rounded-2xl transition"
              style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
              data-testid={`dashboard-event-${ev.id}`}
            >
              <div
                className="text-center shrink-0 px-3 py-1.5 rounded-xl font-mono"
                style={{ background: `${EVENT_TYPE_COLOR[ev.event_type] || "#888"}1a`, color: EVENT_TYPE_COLOR[ev.event_type], minWidth: 88 }}
              >
                <div className="text-[10px] uppercase tracking-wider">{formatEventDate(eventLocalDate(ev))}</div>
                {eventLocalTime(ev) && <div className="text-xs font-semibold mt-0.5">{eventLocalTime(ev)}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{ev.title}</div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap text-[11px]" style={{ color: "var(--text-muted)" }}>
                  <span className="font-mono uppercase tracking-wider" style={{ color: EVENT_TYPE_COLOR[ev.event_type] }}>
                    {EVENT_TYPE_LABEL[ev.event_type]}
                  </span>
                  {ev.duration_minutes && (
                    <span className="flex items-center gap-1"><Clock size={10} /> {ev.duration_minutes}m</span>
                  )}
                  {ev.location && (
                    <span className="flex items-center gap-1 truncate"><MapPin size={10} /> {ev.location}</span>
                  )}
                  {ev.source === "lead" && (
                    <span className="font-mono uppercase tracking-wider" style={{ color: "#60a5fa" }}>From lead</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
          <Link
            to="/calendar"
            className="block text-sm py-3"
            style={{ color: "var(--lime)" }}
            data-testid="dashboard-upcoming-calendar-link"
          >
            Open calendar →
          </Link>
        </div>
      )}
    </div>
  );
}

